/**
 * ╔══════════════════════════════════════════════════════════════╗
 * ║                    APP COLOUR THEMES                         ║
 * ║                                                              ║
 * ║  1. Add / edit palette objects below.                        ║
 * ║  2. Set `activeTheme` to the key you want.                   ║
 * ║  Nothing else needs to be touched.                           ║
 * ╚══════════════════════════════════════════════════════════════╝
 *
 *  primary  →  main accent  (buttons, highlights, links)
 *  light    →  lighter accent (gradients, badges, hover tints)
 *  dark     →  deeper accent  (borders, text derivation)
 *  cream    →  background hint colour
 */

export const themes = {

  burntSienna: {
    name: 'Burnt Sienna',
    primary: '#E35336',   // burnt sienna
    light: '#F4A460',   // sandy brown
    dark: '#A0522D',   // sienna dark
    cream: '#F5F5DC',   // beige
  },

  mossyHollow: {
    name: 'Mossy Hollow',
    primary: '#636B2F',   // olive
    light: '#BAC095',   // sage
    dark: '#3D4127',   // dark forest
    cream: '#D4DE95',   // light lime
  },

  wisteriaBloom: {
    name: 'Wisteria Bloom',
    primary: '#9400D3',   // deep violet
    light: '#ED80E9',   // orchid
    dark: '#D8BFD8',   // thistle
    cream: '#D3D3FF',   // lavender
  },

  oceanBlue: {
    name: 'Ocean Blue',
    primary: '#2563eb',   // royal blue
    light: '#0ea5e9',   // sky blue
    dark: '#1e0b6e',   // deep indigo
    cream: '#e0ecff',   // light blue tint
  },

  lavenderFields: {
    name: 'Lavender Fields',
    primary: '#CF6DFC',   // orchid purple
    light: '#C1BFFF',   // soft lavender
    dark: '#BDB96A',   // olive khaki
    cream: '#FDFBD4',   // light yellow
  },

  goldenTaupe: {
    name: 'Golden Taupe',
    primary: '#D4AF37',   // gold
    light: '#BDB76B',   // dark khaki
    dark: '#CE8946',   // bronze
    cream: '#FDFBD4',   // light yellow
  },

  retroSunset: {
    name: 'Retro Sunset',
    primary: '#BE5103',   // burnt orange
    light: '#FFCE1B',   // golden yellow
    dark: '#B7410E',   // rust
    cream: '#069494',   // teal
  },

  oceanTide: {
    name: 'Ocean Tide',
    primary: '#4052D6',   // cobalt blue
    light: '#00D1D1',   // turquoise
    dark: '#005C5C',   // deep teal
    cream: '#00ADAD',   // medium teal
  },

  siltstone: {
    name: 'Siltstone',
    primary: '#FFB16E',   // soft orange
    light: '#FFF5B8',   // pale yellow
    dark: '#CCA25A',   // golden brown
    cream: '#CBBD93',   // sandy tan
  },

  desertMirage: {
    name: 'Desert Mirage',
    primary: '#E35336',   // terracotta
    light: '#98A869',   // sage green
    dark: '#272757',   // deep navy
    cream: '#C2B280',   // sand
  },

  inkWash: {
    name: 'Ink Wash',
    primary: '#4A4A4A',   // charcoal
    light: '#CBCBCB',   // light grey
    dark: '#6D8196',   // slate blue
    cream: '#FFFFE3',   // off white
  },

  tropicalBliss: {
    name: 'Tropical Bliss',
    primary: '#FF3CAC',   // hot pink
    light: '#F9C74F',   // vivid yellow
    dark: '#7B2FBE',   // deep purple
    cream: '#FBFFD4',   // light lime tint
  },

  auroraBorealis: {
    name: 'Aurora Borealis',
    primary: '#00C49A',   // emerald green
    light: '#7B61FF',   // electric violet
    dark: '#0A2342',   // deep midnight blue
    cream: '#E0FFF7',   // mint tint
  },

  auroraBorealis2: {
    name: 'Aurora Borealis 2',
    primary: '#7B61FF',   // emerald violet
    light: '#00C49A',   // electric green
    dark: '#0A2342',   // deep midnight blue
    cream: '#E0FFF7',   // mint tint
  },

  citrusBurst: {
    name: 'Citrus Burst',
    primary: '#FF6B00',   // vivid orange
    light: '#FFD600',   // electric yellow
    dark: '#C0003C',   // deep crimson
    cream: '#FFFBE6',   // warm cream
  },

  candyPop: {
    name: 'Candy Pop',
    primary: '#E91E8C',   // magenta
    light: '#00E5FF',   // electric cyan
    dark: '#6A0DAD',   // deep violet
    cream: '#FFF0F9',   // blush white
  },

  concreteGold: {
    name: 'Concrete Gold',
    primary: '#F5C518',   // bold yellow
    light: '#FFE566',   // soft yellow
    dark: '#4A4A4A',   // cement grey
    cream: '#EAEAE0',   // concrete off-white
  },

  studioCanvas: {
    name: 'Studio Canvas',
    primary: '#F5C518',   // bold yellow (buttons, accents)
    light: '#C8C8C8',     // cement grey
    dark: '#1E1E1E',      // deep charcoal
    cream: '#D63031',     // vivid crimson
    headerBg: '#2C2C2C',  // flat grey header background
    headerText: '#F5C518', // yellow text/icons on header
  },

  graphiteGold: {
    name: 'Graphite Gold',
    primary: '#F5C518',   // bold yellow from studioCanvas
    light: '#CBCBCB',     // cement grey from inkWash
    dark: '#6D8196',      // slate blue from inkWash
    cream: '#FFFFE3',     // off-white from inkWash
    headerBg: '#2C2C2C',  // flat charcoal header from studioCanvas
    headerText: '#F5C518', // yellow nav text from studioCanvas
  },

};

// ─── Change this one line to switch themes ───────────────────────────────────
// https://www.figma.com/resource-library/color-combinations/
//   Options: 'burntSienna' | 'mossyHollow' | 'wisteriaBloom' | 'oceanBlue' | 'lavenderFields' | 'goldenTaupe' | 'retroSunset' | 'oceanTide' | 'siltstone' | 'desertMirage' | 'inkWash' | 'tropicalBliss' | 'auroraBorealis' | 'auroraBorealis2' | 'citrusBurst' | 'candyPop' | 'concreteGold' | 'studioCanvas' | 'graphiteGold'
export const activeTheme = 'auroraBorealis';
// ─────────────────────────────────────────────────────────────────────────────

export default themes[activeTheme];
